import { useEffect, useMemo, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SlotImage from '@/components/base/SlotImage';
import { useCollection } from '@/hooks/useCollection';
import type { ChurchEvent } from '@/lib/types';
import EventImageCarousel from '@/pages/events/components/EventImageCarousel';

function formatDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(value: string | null): string {
  if (!value) return '';
  const parts = value.split(':');
  const hours = parseInt(parts[0], 10) || 0;
  const mins = parts[1] ?? '00';
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 || 12;
  return `${h}:${mins} ${ampm}`;
}

function dayMonth(value: string | null): { day: string; month: string } {
  if (!value) return { day: '--', month: '' };
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return { day: '--', month: '' };
  return {
    day: d.getDate().toString(),
    month: d.toLocaleDateString('en-US', { month: 'short' }),
  };
}

const CATEGORY_TABS = [
  { value: 'all', label: 'All' },
  { value: 'event', label: 'Events' },
  { value: 'project', label: 'Projects' },
  { value: 'outreach', label: 'Outreach' },
];

export default function EventsPage() {
  const { rows: events, loading, error, reload } = useCollection<ChurchEvent>('events', 'event_date', true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    document.title = 'Events — TGMCI';
    window.scrollTo(0, 0);
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  const { upcoming, past } = useMemo(() => {
    const up = events.filter((e) => !e.event_date || e.event_date >= today);
    const pa = events.filter((e) => e.event_date && e.event_date < today);
    return { upcoming: up, past: pa };
  }, [events, today]);

  const filteredUpcoming = useMemo(() => {
    if (activeCategory === 'all') return upcoming;
    return upcoming.filter((e) => (e.category || 'event') === activeCategory);
  }, [upcoming, activeCategory]);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-20 overflow-hidden bg-background-950 text-center">
          <div className="absolute inset-0">
            <SlotImage
              slot="events_hero"
              fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/d4b76b2d-0b00-4684-9b37-6b17601f9688_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-1.webp"
              alt="Events background"
              className="w-full h-full object-cover object-top opacity-15"
            />
          </div>
          <div className="relative z-10 max-w-[800px] mx-auto px-4">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-3">Events</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              Upcoming Events &amp; Gatherings
            </h1>
            <div className="w-12 h-1 bg-accent-500 mx-auto mb-6" />
            <p className="text-white/60 text-base leading-relaxed">
              Join us at our worship services, outreach programs, and community gatherings throughout the year.
            </p>
          </div>
        </section>

        {/* Upcoming Events */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <h2 className="font-heading text-xl font-semibold text-foreground-700 flex items-center gap-2">
                <i className="ri-calendar-event-line text-primary-600" aria-hidden="true" />
                Upcoming Events
              </h2>

              {/* Category filter */}
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 border border-background-200">
                {CATEGORY_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setActiveCategory(tab.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-label font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      activeCategory === tab.value
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <p className="text-center text-foreground-400 py-16">
                <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading events...
              </p>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-red-600 text-sm mb-3">Could not load events.</p>
                <button type="button" onClick={reload} className="btn-primary">Retry</button>
              </div>
            ) : filteredUpcoming.length === 0 ? (
              <p className="text-center text-foreground-400 py-16">
                <i className="ri-calendar-line text-4xl text-foreground-300" aria-hidden="true" />
                <span className="block mt-3 text-sm">No {activeCategory !== 'all' ? `${activeCategory}s` : 'events'} scheduled. Check back soon!</span>
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredUpcoming.map((ev) => {
                  const dm = dayMonth(ev.event_date);
                  const isProject = (ev.category || 'event') === 'project';
                  return (
                    <article
                      key={ev.id}
                      className="group bg-background-100 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 hover:-translate-y-1 transition-all flex flex-col"
                    >
                      <EventImageCarousel
                        images={ev.images?.length ? ev.images : ev.image ? [ev.image] : []}
                        alt={ev.title}
                        overlay={
                          <>
                            <div className="absolute top-3 left-3 bg-white rounded-lg px-3 py-2 text-center shadow-sm">
                              <div className="font-heading font-bold text-xl text-primary-600 leading-none">{dm.day}</div>
                              <div className="text-[10px] font-label uppercase tracking-wider text-foreground-500">{dm.month}</div>
                            </div>
                            {isProject && (
                              <div className="absolute top-3 right-3 bg-accent-500 text-foreground-950 text-[10px] font-label font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">
                                Project
                              </div>
                            )}
                          </>
                        }
                      />

                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="font-heading font-semibold text-lg text-foreground-800 leading-tight mb-3">
                          {ev.title}
                        </h3>
                        <div className="space-y-1.5 text-sm text-foreground-500 mb-4">
                          {ev.event_date && (
                            <p className="flex items-center gap-2">
                              <i className="ri-calendar-line text-accent-500" aria-hidden="true" />
                              {formatDate(ev.event_date)}
                              {ev.start_time && <span>· {formatTime(ev.start_time)}{ev.end_time ? ` – ${formatTime(ev.end_time)}` : ''}</span>}
                            </p>
                          )}
                          {ev.location && (
                            <p className="flex items-center gap-2">
                              <i className="ri-map-pin-line text-accent-500" aria-hidden="true" />
                              {ev.location}
                            </p>
                          )}
                          {ev.organizer && (
                            <p className="flex items-center gap-2">
                              <i className="ri-user-line text-accent-500" aria-hidden="true" />
                              {ev.organizer}
                            </p>
                          )}
                        </div>
                        {ev.description && (
                          <p className="text-foreground-500 text-sm leading-relaxed line-clamp-3 mb-4">{ev.description}</p>
                        )}
                        <div className="mt-auto space-y-2">
                          {isProject && (
                            <a
                              href="/give"
                              className="inline-flex items-center justify-center gap-1.5 w-full text-sm font-label font-semibold text-foreground-950 bg-accent-500 hover:bg-accent-600 px-4 py-2.5 rounded-md transition-colors whitespace-nowrap"
                            >
                              <i className="ri-heart-3-line" aria-hidden="true" />
                              Contribute to This Project
                            </a>
                          )}
                          {ev.registration_url ? (
                            <a
                              href={ev.registration_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 hover:text-primary-800 transition-colors"
                            >
                              Register / Learn More
                              <i className="ri-arrow-right-line" aria-hidden="true" />
                            </a>
                          ) : (
                            <a href="/contact" className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 hover:text-primary-800 transition-colors">
                              Contact Us
                              <i className="ri-arrow-right-line" aria-hidden="true" />
                            </a>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Past Events */}
        {past.length > 0 && (
          <section className="py-16 bg-background-100">
            <div className="max-w-[1200px] mx-auto px-4 md:px-8">
              <h2 className="font-heading text-xl font-semibold text-foreground-700 mb-8 flex items-center gap-2">
                <i className="ri-history-line text-foreground-400" aria-hidden="true" />
                Past Events
              </h2>
              <div className="space-y-3">
                {past.map((ev) => (
                  <div
                    key={ev.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-background-50 rounded-xl border border-background-200 px-5 py-4"
                  >
                    <div>
                      <h3 className="font-heading font-semibold text-foreground-800">{ev.title}</h3>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {formatDate(ev.event_date)}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </p>
                    </div>
                    {ev.image && (
                      <img src={ev.image} alt="" className="w-14 h-14 rounded-lg object-cover object-top" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 bg-primary-700 text-center px-4">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Have an Event in Mind?</h2>
          <p className="text-white/70 text-base mb-8 max-w-lg mx-auto">
            Planning a gathering, outreach, or community event? We'd love to hear about it.
          </p>
          <a href="/contact" className="btn-accent">
            <i className="ri-chat-1-line" aria-hidden="true" />
            Get in Touch
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}